import { HelpHint } from "../misc/HelpHint";
import { useRef, useState, type FormEvent } from "react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useNotify,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Deal, Sale } from "../types";
import type { CrmDataProvider } from "../providers/types";
import { BUDGET_ZONES, type BudgetZone } from "./adjustmentModel";
import {
  FINANCIAL_ZONES,
  type FinancePlan,
  type ZoneKey,
} from "./financeModel";
import {
  EXPENSE_CATEGORIES,
  EXPENSE_REASONS,
  type ExpenseReason,
} from "./expenseModel";

const today = () => {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

export function AddProjectExpense({
  project,
  plan,
}: {
  project: Deal;
  plan?: FinancePlan;
}) {
  const { identity } = useGetIdentity();
  const provider = useDataProvider<CrmDataProvider>();
  const cache = useQueryClient();
  const notify = useNotify();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState("salary");
  const [amount, setAmount] = useState("");
  const [spentAt, setSpentAt] = useState(today);
  const [note, setNote] = useState("");
  const [zone, setZone] = useState<ZoneKey>("administration");
  const [reason, setReason] = useState<ExpenseReason>("planned");
  const [to, setTo] = useState<BudgetZone>("reserve");
  const [beneficiary, setBeneficiary] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const saving = useRef(false);
  const attempt = useRef<{ signature: string; key: string } | null>(null);
  const sales = useGetList<Sale>(
    "sales",
    {
      filter: { "disabled@neq": true },
      pagination: { page: 1, perPage: 1000 },
      sort: { field: "last_name", order: "ASC" },
    },
    { enabled: open && identity?.administrator === true },
  );
  if (!identity?.administrator) return null;
  const show = () => {
    setCategory("salary");
    setAmount("");
    setSpentAt(today());
    setNote("");
    setZone("administration");
    setReason("planned");
    setTo("reserve");
    setBeneficiary("");
    setError("");
    attempt.current = null;
    setOpen(true);
  };
  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (saving.current || !plan || project.archived_at) return;
    const value = Number(amount);
    if (
      !Number.isFinite(value) ||
      value <= 0 ||
      value > 9999999999.99 ||
      Math.abs(value * 100 - Math.round(value * 100)) > 0.0001
    ) {
      setError("Укажите положительную сумму, до двух знаков после запятой");
      return;
    }
    if (!spentAt || !note.trim() || (category === "salary" && !beneficiary)) {
      setError("Укажите дату, пояснение и получателя зарплаты");
      return;
    }
    if (reason !== "planned" && to === zone) {
      setError("Выберите два разных бюджета");
      return;
    }
    const payload = {
      deal_id: project.id,
      amount: value,
      category,
      spent_at: spentAt,
      note: note.trim(),
      zone_key: zone,
      reason,
      to_zone: reason === "planned" ? null : to,
      beneficiary_sales_id: beneficiary ? Number(beneficiary) : null,
    };
    const signature = JSON.stringify(payload);
    if (attempt.current?.signature !== signature)
      attempt.current = { signature, key: crypto.randomUUID() };
    saving.current = true;
    setBusy(true);
    setError("");
    try {
      await provider.createProjectExpense({
        ...payload,
        submission_key: attempt.current.key,
      });
      await Promise.all(
        [
          "expenses",
          "expense_reviews",
          "project_expense_totals",
          "financial_analytics",
          "project_budget_adjustments",
        ].map((resource) => cache.invalidateQueries({ queryKey: [resource] })),
      );
      setOpen(false);
      notify("Расход сохранён и учтён", { type: "success" });
    } catch (error) {
      setError((error as Error)?.message ?? "Не удалось сохранить расход");
    } finally {
      saving.current = false;
      setBusy(false);
    }
  };
  const selectClass = "block w-full h-10 border rounded-md px-2 bg-background";
  return (
    <>
      <Button
        type="button"
        onClick={show}
        disabled={!plan || !!project.archived_at}
      >
        Добавить расход
      </Button>
      <Dialog
        open={open}
        onOpenChange={(value) => {
          if (!saving.current) setOpen(value);
        }}
      >
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Добавить расход</DialogTitle>
          </DialogHeader>
          <div className="flex items-center justify-between gap-2">
            <span className="text-sm font-medium">{project.name}</span>
            <HelpHint>Расход будет сразу учтён в финансах проекта.</HelpHint>
          </div>
          <form onSubmit={save} className="space-y-4">
            <fieldset disabled={busy} className="space-y-4">
              <label className="block text-sm">
                Категория
                <select
                  className={selectClass}
                  value={category}
                  onChange={(event) => setCategory(event.target.value)}
                >
                  {EXPENSE_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                {category === "salary"
                  ? "Получатель зарплаты"
                  : "Сотрудник (необязательно)"}
                <select
                  required={category === "salary"}
                  className={selectClass}
                  value={beneficiary}
                  onChange={(event) => setBeneficiary(event.target.value)}
                >
                  <option value="">Выберите сотрудника</option>
                  {sales.data?.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.first_name} {s.last_name}
                    </option>
                  ))}
                </select>
              </label>
              {sales.error && (
                <p role="alert">Не удалось загрузить сотрудников</p>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="text-sm">
                  Сумма, ₽
                  <Input
                    required
                    type="number"
                    min="0.01"
                    max="9999999999.99"
                    step="0.01"
                    value={amount}
                    onChange={(event) => setAmount(event.target.value)}
                  />
                </label>
                <label className="text-sm">
                  Дата расхода
                  <Input
                    required
                    type="date"
                    value={spentAt}
                    onChange={(event) => setSpentAt(event.target.value)}
                  />
                </label>
              </div>
              <label className="block text-sm">
                Тип и причина расхода
                <select
                  className={selectClass}
                  value={reason}
                  onChange={(event) =>
                    setReason(event.target.value as ExpenseReason)
                  }
                >
                  {EXPENSE_REASONS.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                Откуда списать
                <select
                  className={selectClass}
                  value={zone}
                  onChange={(event) => {
                    const next = event.target.value as ZoneKey;
                    setZone(next);
                    if (to === next) setTo("reserve");
                  }}
                >
                  {FINANCIAL_ZONES.map((z) => (
                    <option key={z.key} value={z.key}>
                      {z.label}
                    </option>
                  ))}
                </select>
              </label>
              {reason !== "planned" && (
                <label className="block text-sm">
                  Куда добавить
                  <select
                    className={selectClass}
                    value={to}
                    onChange={(event) =>
                      setTo(event.target.value as BudgetZone)
                    }
                  >
                    {BUDGET_ZONES.filter((z) => z.key !== zone).map((z) => (
                      <option key={z.key} value={z.key}>
                        {z.label}
                      </option>
                    ))}
                  </select>
                </label>
              )}
              <HelpHint>
                {reason === "planned"
                  ? "Сумма уменьшит остаток выбранного бюджета."
                  : "Сумма будет перенесена между выбранными бюджетами без повторного списания. Ответственный за исходный бюджет берётся из финансового плана."}
              </HelpHint>
              <label className="block text-sm">
                Пояснение
                <Textarea
                  required
                  maxLength={2000}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                />
              </label>
              {error && (
                <p role="alert" className="text-sm text-destructive">
                  {error}
                </p>
              )}
              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                >
                  Отмена
                </Button>
                <Button
                  type="submit"
                  disabled={!plan || !!project.archived_at || !!sales.error}
                >
                  {busy ? "Сохранение…" : "Сохранить расход"}
                </Button>
              </div>
            </fieldset>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
